import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Bar } from 'react-chartjs-2';
import 'chart.js/auto';
import {
  Box,
  Button,
  Card,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography
} from '@mui/material';

const MONTH_LABELS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'LKR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value || 0);
};

const formatDate = (value) => {
  if (!value) {
    return '';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }

  return parsed.toISOString().split('T')[0];
};

const buildCsv = (headers, rows) => {
  const safeValue = (value) => {
    const text = value === null || value === undefined ? '' : String(value);
    return `"${text.replace(/"/g, '""')}"`;
  };

  const lines = [];
  lines.push(headers.map((header) => safeValue(header.label)).join(','));
  rows.forEach((row) => {
    lines.push(headers.map((header) => safeValue(row[header.key])).join(','));
  });

  return lines.join('\n');
};

const downloadCsv = (filename, headers, rows) => {
  const content = buildCsv(headers, rows);
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

function AnnualSalesBreakdown() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [yearDate, setYearDate] = useState(`${currentYear}-01-01`);
  const [store, setStore] = useState('all');
  const [summary, setSummary] = useState([]);
  const [details, setDetails] = useState([]);
  const [detailsPage, setDetailsPage] = useState(0);
  const [detailsRowsPerPage, setDetailsRowsPerPage] = useState(25);
  const [detailsTotal, setDetailsTotal] = useState(0);
  const [previousTotals, setPreviousTotals] = useState({
    gross_total: 0,
    discount: 0,
    net_total: 0,
    invoice_count: 0
  });
  const [comparisonLoading, setComparisonLoading] = useState(true);
  const [totals, setTotals] = useState({
    gross_total: 0,
    discount: 0,
    net_total: 0,
    invoice_count: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!year) {
      setLoading(false);
      setSummary([]);
      setDetails([]);
      setDetailsTotal(0);
      setPreviousTotals({
        gross_total: 0,
        discount: 0,
        net_total: 0,
        invoice_count: 0
      });
      setComparisonLoading(false);
      setTotals({
        gross_total: 0,
        discount: 0,
        net_total: 0,
        invoice_count: 0
      });
      return;
    }

    setLoading(true);
    setError('');

    axios
      .post('https://bmserver.ccsoftwares.com/Sales/annualBreakdown', {
        year: Number(year),
        store,
        details_page: detailsPage + 1,
        details_limit: detailsRowsPerPage
      })
      .then((res) => {
        const data = res.data || {};
        setSummary(Array.isArray(data.summary) ? data.summary : []);
        setDetails(Array.isArray(data.details) ? data.details : []);
        setDetailsTotal(data.details_pagination?.total || 0);
        setTotals(
          data.totals || {
            gross_total: 0,
            discount: 0,
            net_total: 0,
            invoice_count: 0
          }
        );
      })
      .catch((err) => {
        console.error(err);
        setError('Failed to load annual sales breakdown.');
        setSummary([]);
        setDetails([]);
        setDetailsTotal(0);
        setTotals({
          gross_total: 0,
          discount: 0,
          net_total: 0,
          invoice_count: 0
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [detailsPage, detailsRowsPerPage, year, store]);

  useEffect(() => {
    if (!year) {
      setPreviousTotals({
        gross_total: 0,
        discount: 0,
        net_total: 0,
        invoice_count: 0
      });
      setComparisonLoading(false);
      return;
    }

    setComparisonLoading(true);
    const previousYear = Number(year) - 1;

    axios
      .post('https://bmserver.ccsoftwares.com/Sales/annualBreakdown', {
        year: previousYear,
        store,
        details_page: 1,
        details_limit: 1
      })
      .then((res) => {
        const data = res.data || {};
        setPreviousTotals(
          data.totals || {
            gross_total: 0,
            discount: 0,
            net_total: 0,
            invoice_count: 0
          }
        );
      })
      .catch((err) => {
        console.error(err);
        setPreviousTotals({
          gross_total: 0,
          discount: 0,
          net_total: 0,
          invoice_count: 0
        });
      })
      .finally(() => {
        setComparisonLoading(false);
      });
  }, [store, year]);

  const comparisonChartData = useMemo(() => {
    const previousYearLabel = year ? Number(year) - 1 : 'Last Year';
    const currentLabel = year ? `${year}` : 'This Year';

    return {
      labels: ['Gross Total', 'Net Total'],
      datasets: [
        {
          label: year ? `This Year (${currentLabel})` : 'This Year',
          data: [totals.gross_total || 0, totals.net_total || 0],
          backgroundColor: 'rgba(25, 118, 210, 0.7)'
        },
        {
          label: year ? `Last Year (${previousYearLabel})` : 'Last Year',
          data: [previousTotals.gross_total || 0, previousTotals.net_total || 0],
          backgroundColor: 'rgba(255, 167, 38, 0.7)'
        }
      ]
    };
  }, [previousTotals.gross_total, previousTotals.net_total, totals.gross_total, totals.net_total, year]);

  const comparisonChartOptions = useMemo(() => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom'
        }
      }
    };
  }, []);

  const normalizedSummary = useMemo(() => {
    const summaryByMonth = new Map(summary.map((item) => [item.month, item]));

    return MONTH_LABELS.map((label, index) => {
      const month = index + 1;
      const item = summaryByMonth.get(month);
      return {
        month,
        label,
        invoice_count: item?.invoice_count || 0,
        gross_total: item?.gross_total || 0,
        discount: item?.discount || 0,
        net_total: item?.net_total || 0
      };
    });
  }, [summary]);

  const handleSummaryDownload = () => {
    const rows = normalizedSummary.map((item) => ({
      month: item.label,
      invoice_count: item.invoice_count,
      gross_total: item.gross_total,
      discount: item.discount,
      net_total: item.net_total
    }));

    downloadCsv(
      `annual-sales-summary-${year}.csv`,
      [
        { key: 'month', label: 'Month' },
        { key: 'invoice_count', label: 'Invoices' },
        { key: 'gross_total', label: 'Gross Total' },
        { key: 'discount', label: 'Discount' },
        { key: 'net_total', label: 'Net Total' }
      ],
      rows
    );
  };

  const handleDetailsDownload = () => {
    const rows = details.map((item) => ({
      invoice_no: item.invoice_no,
      store: item.store,
      bill_date: formatDate(item.bill_date),
      gross_total: item.gross_total,
      discount: item.discount,
      net_total: item.net_total
    }));

    downloadCsv(
      `annual-sales-details-${year}.csv`,
      [
        { key: 'invoice_no', label: 'Invoice No' },
        { key: 'store', label: 'Store' },
        { key: 'bill_date', label: 'Bill Date' },
        { key: 'gross_total', label: 'Gross Total' },
        { key: 'discount', label: 'Discount' },
        { key: 'net_total', label: 'Net Total' }
      ],
      rows
    );
  };

  return (
    <Box sx={{ width: '100%', backgroundColor: 'white', p: 2 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item md={3} xs={12}>
          <TextField
            label="Year"
            type="date"
            value={yearDate}
            onChange={(event) => {
              const nextValue = event.target.value;
              if (nextValue === '') {
                setYearDate('');
                setYear('');
                setDetailsPage(0);
                return;
              }
              setYearDate(nextValue);
              const parsed = Number(nextValue.split('-')[0]);
              if (!Number.isNaN(parsed)) {
                setYear(parsed);
                setDetailsPage(0);
              }
            }}
            inputProps={{
              min: '2000-01-01',
              max: `${currentYear + 1}-12-31`
            }}
            InputLabelProps={{
              shrink: true
            }}
            fullWidth
          />
        </Grid>
        <Grid item md={3} xs={12}>
          <InputLabel id="annual-store-label">Store</InputLabel>
          <Select
            labelId="annual-store-label"
            label="Store"
            name="Store"
            value={store}
            onChange={(event) => {
              setStore(event.target.value);
              setDetailsPage(0);
            }}
            fullWidth
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="Warakapola">Warakapola</MenuItem>
            <MenuItem value="Galigamuwa">Galigamuwa</MenuItem>
          </Select>
        </Grid>
        <Grid
          item
          md={6}
          xs={12}
          sx={{
            display: 'flex',
            gap: 2,
            justifyContent: { md: 'flex-end', xs: 'flex-start' }
          }}
        >
          <Button
            variant="outlined"
            onClick={handleSummaryDownload}
            disabled={loading || !year}
          >
            Download Summary CSV
          </Button>
          <Button
            variant="contained"
            onClick={handleDetailsDownload}
            disabled={loading || !year || details.length === 0}
          >
            Download Details CSV
          </Button>
        </Grid>
      </Grid>

      {error && (
        <Typography sx={{ mt: 2 }} color="error">
          {error}
        </Typography>
      )}

      {loading ? (
        <Typography sx={{ mt: 2 }}>Loading...</Typography>
      ) : (
        <>
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Yearly Gross vs Net Comparison
            </Typography>
            <Box sx={{ height: 280 }}>
              {comparisonLoading ? (
                <Typography>Loading chart...</Typography>
              ) : (
                <Bar data={comparisonChartData} options={comparisonChartOptions} />
              )}
            </Box>
          </Box>

          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item md={3} xs={12}>
              <Card
                sx={{
                  height: 100,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                Net Total: {formatCurrency(totals.net_total)}
              </Card>
            </Grid>
            <Grid item md={3} xs={12}>
              <Card
                sx={{
                  height: 100,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                Gross Total: {formatCurrency(totals.gross_total)}
              </Card>
            </Grid>
            <Grid item md={3} xs={12}>
              <Card
                sx={{
                  height: 100,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                Discount: {formatCurrency(totals.discount)}
              </Card>
            </Grid>
            <Grid item md={3} xs={12}>
              <Card
                sx={{
                  height: 100,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                Invoices: {totals.invoice_count || 0}
              </Card>
            </Grid>
          </Grid>

          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Monthly Summary
            </Typography>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Month</TableCell>
                    <TableCell align="right">Invoices</TableCell>
                    <TableCell align="right">Gross Total</TableCell>
                    <TableCell align="right">Discount</TableCell>
                    <TableCell align="right">Net Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {normalizedSummary.map((row) => (
                    <TableRow key={row.month}>
                      <TableCell>{row.label}</TableCell>
                      <TableCell align="right">{row.invoice_count}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(row.gross_total)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(row.discount)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(row.net_total)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Invoice Details
            </Typography>
            <TableContainer component={Paper} sx={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Invoice No</TableCell>
                    <TableCell>Store</TableCell>
                    <TableCell>Bill Date</TableCell>
                    <TableCell align="right">Gross Total</TableCell>
                    <TableCell align="right">Discount</TableCell>
                    <TableCell align="right">Net Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {details.map((row, index) => (
                    <TableRow key={`${row.invoice_no}-${index}`}>
                      <TableCell>{row.invoice_no}</TableCell>
                      <TableCell>{row.store}</TableCell>
                      <TableCell>{formatDate(row.bill_date)}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(row.gross_total)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(row.discount)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(row.net_total)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {details.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No sales found for this year.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={detailsTotal}
              page={detailsPage}
              onPageChange={(event, newPage) => setDetailsPage(newPage)}
              rowsPerPage={detailsRowsPerPage}
              onRowsPerPageChange={(event) => {
                setDetailsRowsPerPage(parseInt(event.target.value, 10));
                setDetailsPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </Box>
        </>
      )}
    </Box>
  );
}

export default AnnualSalesBreakdown;
